import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Send, Bot, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Agent, AGENT_TYPE_INFO } from "../../types/agent";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { agentApi } from "../../services/agentApi";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: Date;
  isTyping?: boolean;
}

interface AgentChatInterfaceProps {
  agent: Agent;
  onClose: () => void;
}

export function AgentChatInterface({
  agent,
  onClose,
}: AgentChatInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const removeReasoning = (content: string): string => {
    // Remove reasoning blocks and clean up extra whitespace
    return content
      .replace(/<reasoning>[\s\S]*?<\/reasoning>/gi, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputText.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsLoading(true);

    // Add typing indicator
    const typingMessage: Message = {
      id: "typing",
      content: "",
      sender: "assistant",
      timestamp: new Date(),
      isTyping: true,
    };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      const response = await agentApi.testAgent(agent.id, {
        message: userMessage.content,
        conversation_id: conversationId || undefined,
        validate_output: false,
        use_pydantic_ai: false,
      });

      // Store conversation ID for future messages
      if (!conversationId && response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Remove typing indicator and add actual response
      setMessages((prev) =>
        prev
          .filter((msg) => msg.id !== "typing")
          .concat({
            id: (Date.now() + 1).toString(),
            content: removeReasoning(response.response),
            sender: "assistant",
            timestamp: new Date(),
          })
      );
    } catch (error) {
      console.error("Failed to send message:", error);

      // Remove typing indicator and show error
      setMessages((prev) =>
        prev
          .filter((msg) => msg.id !== "typing")
          .concat({
            id: (Date.now() + 1).toString(),
            content:
              "I apologize, but I encountered an error processing your message. Please try again.",
            sender: "assistant",
            timestamp: new Date(),
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="bg-brand-bg border-b border-brand-border p-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2">
          <Bot className="h-6 w-6 text-brand-primary" />
          <div>
            <h2 className="font-semibold text-brand-text">{agent.name}</h2>
            <div className="flex items-center gap-2 text-sm text-brand-muted">
              <span
                className={`px-2 py-1 text-xs rounded ${
                  AGENT_TYPE_INFO[agent.agent_type]?.color ||
                  "bg-brand-surface text-brand-text"
                }`}
              >
                {AGENT_TYPE_INFO[agent.agent_type]?.label || agent.agent_type}
              </span>
              <span className="text-brand-teal">● Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-brand-surface">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] lg:max-w-[75%] px-4 py-3 rounded-card ${
                message.sender === "user"
                  ? "bg-brand-primary text-brand-surface"
                  : "bg-brand-bg text-brand-text shadow-sm"
              }`}
            >
              {message.isTyping ? (
                <div className="flex items-center gap-1">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-brand-muted rounded-pill animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-brand-muted rounded-pill animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-brand-muted rounded-pill animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                  <span className="text-sm text-brand-muted ml-2">
                    Thinking...
                  </span>
                </div>
              ) : (
                <div>
                  {message.sender === "assistant" ? (
                    <div className="prose prose-sm max-w-none prose-gray prose-table:text-xs prose-th:p-2 prose-td:p-2 prose-table:border-collapse">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {removeReasoning(message.content)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  )}
                  <p
                    className={`text-xs mt-1 ${
                      message.sender === "user"
                        ? "text-brand-surface/70"
                        : "text-brand-muted"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-brand-bg border-t border-brand-border p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question ou discutez avec moi..."
              className="w-full px-4 py-3 border border-brand-border rounded-card focus:ring-2 focus:ring-brand-primary focus:border-brand-primary resize-none"
              rows={1}
              disabled={isLoading}
              style={{
                minHeight: "44px",
                maxHeight: "120px",
                resize: "none",
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = Math.min(target.scrollHeight, 120) + "px";
              }}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading}
            className="px-4 py-3 bg-brand-primary text-brand-surface hover:opacity-90 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex items-center justify-between mt-2 text-xs text-brand-muted">
          <span>Press Enter to send, Shift+Enter for new line</span>
          {conversationId && (
            <span>Conversation ID: {conversationId.slice(-8)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
